import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import {
  Alert, Button, Icon, Row,
} from '@openedx/paragon';
import { Error } from '@openedx/paragon/icons';

import { LoadingSpinner } from '../../../generic/Loading';
import CardItem from '../../card-item';
import AlertMessage from '../../../generic/alert-message';
import { getStudioHomeLibrariesParams } from '../../data/selectors';
import { updateStudioHomeLibrariesCustomParams } from '../../data/slice';
import { fetchLibraryData } from '../../data/thunks';
import messages from '../messages';
import LibrariesFilters from './libraries-filters';
import '../courses-tab/index.scss';

const LibrariesTab = ({
  libraries,
  isLoading,
  isFailed,
  dispatch,
  isLibraryFiltersEnabled,
  // injected
  intl,
}) => {
  const { isFiltered } = useSelector(getStudioHomeLibrariesParams);

  const handleCleanFilters = () => {
    const customParams = {
      search: undefined,
      order: 'display_name',
      isFiltered: true,
      cleanFilters: true,
      language: undefined,
      course: undefined,
    };

    dispatch(fetchLibraryData({ order: 'display_name' }));
    dispatch(updateStudioHomeLibrariesCustomParams(customParams));
  };

  const hasLibraries = libraries?.length > 0;

  if (isLoading && !isFiltered) {
    return (
      <Row className="m-0 mt-4 justify-content-center">
        <LoadingSpinner />
      </Row>
    );
  }

  return (
    isFailed && !isFiltered ? (
      <AlertMessage
        variant="danger"
        description={(
          <Row className="m-0 align-items-center">
            <Icon src={Error} className="text-danger-500 mr-1" />
            <span>{intl.formatMessage(messages.librariesTabErrorMessage)}</span>
          </Row>
        )}
      />
    ) : (
      <div className="courses-tab-container">
        <LibrariesFilters
          dispatch={dispatch}
          isLoading={isLoading}
          isLibraryFiltersEnabled={isLibraryFiltersEnabled}
        />
        {isLoading ? (
          <Row className="m-0 mt-4 justify-content-center">
            <LoadingSpinner />
          </Row>
        ) : isFailed ? (
          <AlertMessage
            variant="danger"
            description={(
              <Row className="m-0 align-items-center">
                <Icon src={Error} className="text-danger-500 mr-1" />
                <span>{intl.formatMessage(messages.librariesTabErrorMessage)}</span>
              </Row>
            )}
          />
        ) : hasLibraries ? (
          libraries.map(({
            displayName, org, number, url,
          }) => (
            <CardItem
              key={`${org}+${number}`}
              isLibraries
              displayName={displayName}
              org={org}
              number={number}
              url={url}
            />
          ))
        ) : null}

        {isFiltered && !hasLibraries && !isLoading && (
          <Alert className="mt-4">
            <Alert.Heading>
              {intl.formatMessage(messages.coursesTabCourseNotFoundAlertTitle)}
            </Alert.Heading>
            <p data-testid="libraries-not-found-alert">
              {intl.formatMessage(messages.coursesTabCourseNotFoundAlertMessage)}
            </p>
            <Button variant="primary" onClick={handleCleanFilters}>
              {intl.formatMessage(messages.coursesTabCourseNotFoundAlertCleanFiltersButton)}
            </Button>
          </Alert>
        )}
      </div>
    )
  );
};

LibrariesTab.defaultProps = {
  isLibraryFiltersEnabled: true,
};

LibrariesTab.propTypes = {
  libraries: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      libraryKey: PropTypes.string.isRequired,
      number: PropTypes.string.isRequired,
      org: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isFailed: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  isLibraryFiltersEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(LibrariesTab);
